/* ============================================================================
   WAKA Viento — storefront assistant (vanilla, no framework).

   NATIVE PANE (not an iframe). The current production site embeds Viento in an
   iframe; this theme renders it as a first-class slide-over in the DOM so it
   shares the page's fonts/tokens, can read PDP/cart state, and opens instantly.

   Two-speed brain, per the Cowork handoff §5:
   • CANNED (instant)  — general context we already know: board sizing, the
     4-dealer network, the fold/travel story, bag overview, greeting. Rendered
     locally with zero round-trip so Viento feels instant.
   • PROXY  (live)     — order tracking, live stock/INVENTORY, personalised
     foil/wing setups, and anything we don't recognise. POSTed to the App Proxy
     (/apps/viento/ask); responses come from the app server.

   window.Viento = { open, close, ask, seed }
     ask(text)  — opens, brief "typing", then answers (canned or proxy)
     seed(text) — opens with the question + answer landing together (instant);
                  for known context wired into running copy via
                  <a class="viento-link" data-viento-seed="…">Viento</a>

   SINGLE SOURCE OF TRUTH: product/dealer facts (size rows, dealers, board
   cards, prices, ship windows) should be injected by Liquid from metafields
   via window.WAKA_VIENTO_DATA so the storefront and app server read one source.
   The defaults below are conservative fallbacks ONLY — never hardcode prices
   here; board cards render only when Liquid supplies them.
   ============================================================================ */
(function () {
  var panel  = document.getElementById('VientoPanel');
  var scrim  = document.querySelector('[data-viento-scrim]');
  var stream = document.getElementById('VientoStream');
  var input  = document.getElementById('VientoInput');
  if (!panel || !stream) return;

  /* ---- injected data (Liquid → metafields) with safe defaults ---- */
  var DATA = window.WAKA_VIENTO_DATA || {};
  // Live answers come from the waka-agent `chat` Firebase function (same browser→CORS
  // pattern as getInventoryStatus). Requires `cors: WAKA_ORIGIN_RE` on the chat function.
  // Override the endpoint via WAKA_VIENTO_DATA.proxyUrl (e.g. an app proxy) if preferred.
  var PROXY_URL = DATA.proxyUrl || 'https://us-central1-waka-agent.cloudfunctions.net/chat';
  var VIENTO_CONV_ID = null;        // persists the conversation across turns this session
  var VIENTO_HISTORY = [];          // [{ role:'user'|'assistant', content }]
  // Session persistence: the visible transcript + model history survive
  // in-tab navigation (e.g. clicking "View board" then reopening Viento).
  // sessionStorage = per-tab, cleared when the tab closes (privacy-sensible).
  var VIENTO_SESSION_KEY = 'waka_viento_session_v1';
  var VIENTO_TRANSCRIPT = [];        // [{u:text} | {b:parts}] — replayed on load
  var VIENTO_REPLAYING = false;      // suppress save/record while replaying
  var vLastBag = false;              // last canned reply was bag-related (for sizing follow-ups)
  var vLastKg = null;               // last weight captured in the canned size flow (for skill refine)
  var vLastKgLabel = null;          // how the rider phrased it ("75–95 kg") — shown in the recap
  var vLastSkill = null;            // 0 beginner · 1 intermediate · 2 advanced, captured before weight
  // Size table = the PDP "Full specification" table, nothing more (Nigel
  // 2026-09-02): Volume · Length · Width · Weight · Typical rider. NO skill
  // labels — the old rows ("66L · advanced", "99L · all-rounder") asserted a
  // skill fit before the rider had said anything. Mirrors the `geometry` capture
  // in sections/product-size-table.liquid (width in → cm, weight at 1 dp):
  //   66L 183/48 5.10 0–70 · 77L 190/50 5.64 65–85 · 88L 198/52 6.29 75–95 ·
  //   99L 206/54 6.85 85–110 · 111L 213/56 7.40 95+
  var SIZE_GEO = DATA.sizeGeo || [
    { vol:'66L',  lenFt:"6'0\"", lenCm:183, widIn:19,    lo:0,  hi:70  },
    { vol:'77L',  lenFt:"6'3\"", lenCm:190, widIn:19.75, lo:65, hi:85  },
    { vol:'88L',  lenFt:"6'6\"", lenCm:198, widIn:20.5,  lo:75, hi:95  },
    { vol:'99L',  lenFt:"6'9\"", lenCm:206, widIn:21.25, lo:85, hi:110 },
    { vol:'111L', lenFt:"7'0\"", lenCm:213, widIn:22,    lo:95, hi:999 }
  ];
  // Unit system — same rule as the PDP picker (sections/product-size-table.liquid):
  // US defaults to imperial, everyone else metric; the switch on any size guide
  // flips every guide in the stream. Rider ranges convert too (kg ⇄ lb).
  var KG_LB = 2.2046;
  var vMetric = (DATA.location || '').toUpperCase() !== 'US';
  function fmtLen(g){ return vMetric ? g.lenCm + ' cm' : g.lenFt; }
  function fmtWid(g){ return vMetric ? Math.round(g.widIn * 2.54) + ' cm' : g.widIn + '"'; }
  function riderRange(g){
    var u = vMetric ? 'kg' : 'lb';
    var lo = vMetric ? g.lo : Math.round(g.lo * KG_LB), hi = vMetric ? g.hi : Math.round(g.hi * KG_LB);
    return g.lo === 0 ? 'Under ' + hi + ' ' + u : g.hi >= 999 ? lo + ' ' + u + '+' : lo + '\u2013' + hi + ' ' + u;
  }
  function geoFor(vol){ for (var i=0;i<SIZE_GEO.length;i++) if (SIZE_GEO[i].vol===vol) return SIZE_GEO[i]; return null; }
  // The size guide: Volume · Length · Width · Typical rider, with the cm·kg / in·lb
  // switch. Board weight deliberately left out (PDP detail, not a sizing signal).
  // Drawn ONCE per conversation; the weight step uses the strip, not the table.
  function sizeTable(kg){
    var lift = [];
    if (kg != null) SIZE_GEO.forEach(function(g,i){ if (kg >= g.lo && kg <= g.hi) lift.push(i); });
    return { type:'sizeguide', title:'WAKA Glide · size guide', lift:lift };
  }
  // Weight-only step: just the sizes in play, as tokens.
  function rangeStrip(kg){
    return { type:'strip', items: SIZE_GEO.filter(function(g){ return kg >= g.lo && kg <= g.hi; }).map(function(g){ return { vol:g.vol, geo:g }; }) };
  }
  function sizesFor(kg){ return SIZE_GEO.filter(function(g){ return kg >= g.lo && kg <= g.hi; }).map(function(g){ return g.vol; }); }
  // The whole dealer network — small, hand-picked. Override via WAKA_VIENTO_DATA.dealers.
  var DEALERS = DATA.dealers || [
    { name: 'Windance',  loc: 'Hood River, Oregon', cc: 'us', url: 'https://www.windance.com/products/waka-glide-folding-foil-board-2026' },
    { name: 'MACkite',   loc: 'Michigan',           cc: 'us', url: 'https://www.mackiteboarding.com/2025-waka-glide-v1-foil-board/' },
    { name: 'Green Hat', loc: 'United States',       cc: 'us', url: 'https://greenhatkiteboarding.com/products/waka-glide-folding-foilboard' },
    { name: '30 Noeuds', loc: 'Canada',             cc: 'ca', url: 'https://30noeuds.com/en/products/waka-glide-folding-foil-board' }
  ];
  // Board cards — MUST be supplied by Liquid (title, meta, desc, price, url, img).
  // No price is hardcoded here on purpose; if absent, "show me boards" defers to the proxy.
  var CARDS = DATA.cards || null;
  // Board snippets — supplied by Liquid (per-variant specs + price from waka.* metafields).
  // Not hardcoded (prices vary by size) — "tell me about the 99L" defers to the proxy if absent.
  var BOARDS = DATA.boards || null;
  // Bag products (Rolling WAKA, Day Bag) for the snippet pane — same Liquid source.
  var BAGS = DATA.bags || null;

  /* ---- Phase 2 canned data (override via WAKA_VIENTO_DATA) ---- */
  // Warranty — verified 1-year limited warranty (waka.surf/pages/warranty-policy, 2026-06-07).
  var WARRANTY = DATA.warranty || {
    verdict: 'ok', title: '1-year limited warranty', sub: 'Original buyer · against manufacturing defects',
    covered: [
      { y: true,  t: 'Genuine manufacturing defects — carbon board, hull & lamination' },
      { y: true,  t: 'TravelFold™ hinge & locking mechanism' },
      { y: true,  t: 'Titanium fasteners & bulkhead inserts' },
      { y: false, t: 'Impact, hard landings, or hitting the bottom' },
      { y: false, t: 'Normal wear — scratches, scuffs, pad wear' },
      { y: false, t: 'Heat damage, or folding/storing with foot straps in' }
    ],
    period: [['12', 'months · from purchase']],
    step: 'Contact the crew <b>before</b> sending anything back — message us with your order number and a photo or two, and a human on our team takes it from there.'
  };
  var FOLD = DATA.fold || [
    { n: 1, t: 'Loosen the three bolts', d: 'Use the packed fold tool to back off the titanium bolts at the hinge.' },
    { n: 2, t: 'Fold at the hinge', d: 'It folds in half on the carbon-kevlar living hinge — down to about half its length.' },
    { n: 3, t: 'Stow & re-torque', d: 'Slide it into the day bag or Rolling WAKA. Re-torque the bolts before you ride — it locks rock-solid.' }
  ];
  // Registration — QR-scan model (no app to download, no account to set up).
  var REGISTER = DATA.register || [
    { n: 1, t: 'Scan the QR on your board', d: 'It’s by the WAKA logo band — scanning opens registration with your serial already filled in.' },
    { n: 2, t: 'Sign in with Apple or Google', d: 'Your name and email come over — there’s nothing to type.' },
    { n: 3, t: 'Confirm where you bought it', d: 'Tap register. Your board’s on your account and your warranty’s active from day one.' }
  ];
  // Returns — 30-day, unridden, original packaging + proof of purchase.
  var RETURN_STEPS = DATA.returnSteps || [
    { n: 1, t: 'Get in touch first', d: 'Message us within 30 days — unridden, in its original packaging, with your proof of purchase.' },
    { n: 2, t: 'We arrange the return', d: "We'll sort the details and cover the next steps with you." },
    { n: 3, t: 'Refund or swap', d: 'Choose a different size or a full refund once it’s back with us.' }
  ];
  // Returns / warranty-claim flow data.
  var WK_RE = /^wk-?\d{4,6}$/i;                       // a waka.surf order looks like WK-10482
  var SWAP_SIZES = DATA.swapSizes || ['66L','77L','88L','99L','111L'];
  var CLAIM_ISSUES = DATA.claimIssues || [
    { k:'hinge',    t:'TravelFold™ hinge or lock',            cover:'yes' },
    { k:'board',    t:'Board, hull or lamination',            cover:'yes' },
    { k:'hardware', t:'Fasteners or foil-track hardware',     cover:'yes' },
    { k:'impact',   t:'Impact, collision or a ding',          cover:'no' },
    { k:'transit',  t:'Damage in transit (airline / courier)', cover:'transit' }
  ];
  // 🚨 POLICY MIRROR — must match ~/dev/waka-agent/functions/src/config/shipping-dates.ts
  // (`details` per market) and waka.surf/policies/shipping-policy. Found stale
  // 2026-09-02 (EU/UK still "Flat 100 €/£" eight weeks after the 2026-07-13
  // free-shipping decision). Board / board + bag only — standalone bags are
  // calculated at checkout. No stock claims here: availability is live data.
  var SHIP_ALL = DATA.shipping || [
    { cc: 'us', name: 'United States',  note: 'Free on pre-orders',            cost: 'Flat $100',    eta: '3–7 days' },
    { cc: 'ca', name: 'Canada',         note: 'From our Montreal warehouse',   cost: 'Flat CA$100',  eta: '3–7 days' },
    { cc: 'au', name: 'Australia',      note: 'Free on pre-orders',            cost: 'Flat AU$100',  eta: '3–7 business days' },
    { cc: 'nz', name: 'New Zealand',    note: 'Free on pre-orders',            cost: 'Flat NZ$100',  eta: '3–7 business days' },
    { cc: 'eu', name: 'Europe',         note: 'From our EU warehouse (NL)',    cost: 'FREE',         eta: '' },
    { cc: 'gb', name: 'United Kingdom', note: 'From our EU warehouse (NL)',    cost: 'FREE',         eta: '' },
    { cc: 'ch', name: 'Switzerland',    note: 'From our EU warehouse (NL)',    cost: 'FREE',         eta: '' }
  ];
  var SHIP_FOOT = DATA.shipFoot || 'Boards ship <b>free</b> across Europe, the UK and Switzerland — our launch offer. Elsewhere it’s a flat <b>100</b> in your local currency, and <b>free on pre-orders</b> in the US, Australia and New Zealand. Bags on their own are calculated at checkout.';

  /* ---- panel open/close ---- */
  function open(){ panel.classList.add('open'); panel.setAttribute('aria-hidden','false'); if(scrim) scrim.classList.add('open'); document.body.style.overflow='hidden'; greetOnce(); }
  function close(){ panel.classList.remove('open'); panel.setAttribute('aria-hidden','true'); if(scrim) scrim.classList.remove('open'); document.body.style.overflow=''; }

  var greeted = false;
  function greetOnce(){
    if (greeted) return; greeted = true;
    if (stream.children.length === 0) renderBot(cannedRespond('kia ora'));
  }

  function saveSession(){
    if (VIENTO_REPLAYING) return;
    try {
      sessionStorage.setItem(VIENTO_SESSION_KEY, JSON.stringify({
        convId: VIENTO_CONV_ID, history: VIENTO_HISTORY, transcript: VIENTO_TRANSCRIPT
      }));
    } catch (e) { /* storage full / disabled — degrade to in-memory only */ }
  }
  // Replay a saved conversation into the panel on load, before any greeting, so
  // navigating away and back keeps the thread (visible transcript + model context).
  function restoreSession(){
    var raw; try { raw = sessionStorage.getItem(VIENTO_SESSION_KEY); } catch (e) { return; }
    if (!raw) return;
    var s; try { s = JSON.parse(raw); } catch (e) { return; }
    if (!s || !Array.isArray(s.transcript) || !s.transcript.length) return;
    VIENTO_CONV_ID = s.convId || null;
    VIENTO_HISTORY = Array.isArray(s.history) ? s.history : [];
    VIENTO_REPLAYING = true;
    s.transcript.forEach(function(item){
      if (item && item.u != null) addUser(item.u);
      else if (item && item.b) renderBot(item.b);
    });
    VIENTO_REPLAYING = false;
    VIENTO_TRANSCRIPT = s.transcript;
    greeted = true;   // don't paint the greeting over a restored conversation
  }

  /* ---- public API ---- */
  function ask(text){
    open(); var t = (text||'').trim(); if(!t) return;
    addUser(t);
    // Once the agent is engaged (a live conversation exists), keep every turn with
    // it — don't let the canned matcher hijack a reply to the agent's own question
    // (e.g. answering "90kg, both, intermediate" during a foil-setup flow must not
    // re-trigger the board size-guide). Canned is the pre-agent shortcut only.
    var canned = VIENTO_CONV_ID ? null : cannedRespond(t);
    if (canned){ recordAssistant(canned); typingThen(function(){ renderBot(canned); }); }
    else { proxyAsk(t); }
  }
  // seed: instant canned reply (no typing dots) — question + answer land together.
  function seed(text){
    open(); var t = (text||'').trim(); if(!t) return;
    addUser(t);
    var canned = VIENTO_CONV_ID ? null : cannedRespond(t);   // stay with the agent once engaged
    if (canned){ recordAssistant(canned); renderBot(canned); }
    else { proxyAsk(t); }   // unknown seed → still goes live
  }

  /* ---- Live answers (waka-agent `chat` function) ---- */
  var NET_FAIL = "I'm having trouble reaching the crew right now — try again in a moment.";

  var vNextTypingLabel = null;   // one-shot label for the next proxy call's typing bubble
  function proxyAsk(text){
    var typing = showTyping(vNextTypingLabel); vNextTypingLabel = null;
    // (user turn already recorded by addUser, which precedes every proxyAsk call)
    var body = {
      messages: VIENTO_HISTORY,
      conversationId: VIENTO_CONV_ID,
      channel: 'website',
      location: DATA.location || null,
      stream: true
    };
    fetch(PROXY_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json', 'Accept':'text/event-stream' },
      body: JSON.stringify(body)
    })
      .then(function(r){
        var ctype = (r.headers.get('content-type') || '');
        // Stream when the server speaks SSE; otherwise take one JSON blob
        // (older server, a buffering proxy, or a rate-limit/error response).
        if (r.body && r.body.getReader && ctype.indexOf('text/event-stream') !== -1) {
          return consumeSSE(r, typing);
        }
        return r.json().then(function(data){ removeTyping(typing); finishFromData(data); });
      })
      .catch(function(){
        removeTyping(typing);
        var fb = vOfflineFallback; vOfflineFallback = null;
        if (fb){ recordAssistant(fb); renderBot(withBoardCards(fb)); return; }
        renderBot([{ type:'text', text: NET_FAIL }]);
      });
  }

  // Render a final server payload (parts or message). Shared by the SSE `done`
  // event and the non-streaming JSON fallback so both behave identically.
  function finishFromData(data){
    vOfflineFallback = null;
    if (data && data.conversationId) VIENTO_CONV_ID = data.conversationId;
    if (data && data.error && !(data.parts)) { renderBot([{ type:'text', text: data.error }]); return; }
    if (data && Array.isArray(data.parts)) {
      // Record the assistant turn so the model sees its own prior replies on the
      // next turn. The server's context is the client-sent history (it does NOT
      // backfill from Firestore), and the live agent always returns `parts` — so
      // without this the model had amnesia across turns (re-asking, repeating).
      var amsg = (data.message) || textFromParts(data.parts);
      if (amsg) VIENTO_HISTORY.push({ role:'assistant', content: amsg });
      renderBot(withBoardCards(data.parts));
      return;
    }
    var msg = data && (data.message || data.reply);
    if (msg){ VIENTO_HISTORY.push({ role:'assistant', content: msg }); renderBot([{ type:'text', text: msg }]); }
    else { renderBot([{ type:'text', text: '…' }]); }
  }
  // After a `compare` (from the agent or the offline calculator), add the board
  // cards for the picks — live price in the shopper's currency, stock state and
  // the Add-to-cart / View-board CTAs — from the Liquid-injected BOARDS. The
  // server can't price in EUR/GBP/CHF, which is why its prose used to say
  // "see product page for price" (Nigel, 2026-09-02: show price + stock + CTA).
  function withBoardCards(parts){
    if (!Array.isArray(parts)) return parts;
    // The compare part becomes `picks`: one merged card per board — role, volume,
    // why, L · W · rider (unit-aware), live price + stock from Liquid BOARDS, CTAs.
    // Replaces the old compare + two product cards (design pass 2026-09-02).
    var out = [], recVol = null;
    parts.forEach(function(p){
      if (p && p.type === 'compare' && p.picks){
        var items = p.picks.map(function(pk){
          if (pk.rec) recVol = pk.vol;
          return { vol:pk.vol, role:pk.role, tone:pk.tone, why:pk.why, rec:!!pk.rec, tag:pk.tag, geo:geoFor(pk.vol), board:(BOARDS && BOARDS[pk.vol]) || null };
        });
        out.push({ type:'picks', header:p.header, note:p.note, items:items });
      } else out.push(p);
    });
    // Follow-ups as chips, not as a closing question in the prose.
    if (out.some(function(p){ return p.type === 'picks'; }) && !out.some(function(p){ return p.type === 'chips'; })){
      var picksPart = out.filter(function(p){ return p.type === 'picks'; })[0];
      var vols = picksPart.items.map(function(i){ return i.vol; });
      var ch = [];
      if (recVol) ch.push('Why the ' + recVol + '?');
      else if (vols.length === 2) ch.push('Which one for me?');
      ch.push(vols.length === 2 ? 'Email me both options' : 'Email me this option');
      ch.push('Foil & wing setup');
      out.push({ type:'chips', options: ch });
    }
    return out;
  }
  function textFromParts(parts){
    return (parts||[]).filter(function(p){ return p && p.type === 'text' && p.text; })
      .map(function(p){ return p.text; }).join('\n\n');
  }
  // Record a canned reply into the model history so a later hand-off to the
  // agent sees a coherent user/assistant thread (not just the user turns).
  // Uses seedSummary (not textFromParts) so recap/compare panes — the rider's
  // profile and the recommended picks — survive into the history too; plain
  // text-only replies summarise identically to before.
  function recordAssistant(parts){
    if (VIENTO_REPLAYING) return;
    var t = seedSummary(parts);
    if (t) VIENTO_HISTORY.push({ role:'assistant', content: t });
  }

  // Consume the SSE stream: show a live plaintext preview as tokens arrive
  // (status events reset it between turns), then render the authoritative parts
  // — panes + HTML text — on `done`, replacing the preview.
  function consumeSSE(resp, typing){
    var reader = resp.body.getReader();
    var dec = new TextDecoder();
    var buf = '', live = null, liveBody = null, liveText = '', settled = false;

    function ensureLive(){
      if (live) return;
      removeTyping(typing);
      live = el('div','vmsg bot');
      live.appendChild(el('div','vtag','<span class="vt-mk"></span>Viento'));
      // Match the real text part (renderPart → <p class="vtext"> via mdLite) so
      // the preview font/styling is identical and HTML renders instead of showing
      // raw tags. Status labels are plain text (set via textContent below).
      liveBody = el('p','vtext','');
      live.appendChild(liveBody);
      stream.appendChild(live); scrollDown();
    }
    function dropLive(){
      if (live && live.parentNode) live.parentNode.removeChild(live);
      else removeTyping(typing);
    }
    function onEvent(o){
      if (!o || !o.type) return;
      if (o.type === 'status'){ ensureLive(); liveText = ''; liveBody.textContent = o.label || ''; scrollDown(); }
      // Render deltas as HTML through the same mdLite pipeline the final text
      // uses; drop a trailing half-open tag (e.g. "…<ta") so no raw markup flashes.
      else if (o.type === 'delta'){ ensureLive(); liveText += (o.text || ''); liveBody.innerHTML = mdLite(liveText.replace(/<[^>]*$/, '')); scrollDown(); }
      else if (o.type === 'done'){ if (settled) return; settled = true; dropLive(); finishFromData(o.result); }
      else if (o.type === 'error'){ if (settled) return; settled = true; dropLive(); renderBot([{ type:'text', text: NET_FAIL }]); }
    }
    function pump(){
      return reader.read().then(function(res){
        if (res.done){
          if (!settled){
            settled = true; dropLive();
            // Stream ended without a `done` — show whatever streamed, else fail.
            renderBot([{ type:'text', text: liveText || NET_FAIL }]);
          }
          return;
        }
        buf += dec.decode(res.value, { stream:true });
        var chunks = buf.split('\n\n'); buf = chunks.pop();
        chunks.forEach(function(c){
          var i = c.indexOf('data:'); if (i === -1) return;
          var payload = c.slice(i + 5).trim(); if (!payload) return;
          try { onEvent(JSON.parse(payload)); } catch(e){}
        });
        return pump();
      });
    }
    return pump();
  }

  /* ============================================================
     CANNED ENGINE — returns an array of rich parts, or null to
     defer to the proxy (order tracking, live stock, setups, unknown).
     ============================================================ */
  var SKILL_CHIPS  = ['Getting up on foil (beginner)','Foiling & gybing (intermediate)','Tacking & pumping (advanced)'];
  var WEIGHT_CHIPS = vMetric ? ['I weigh under 75 kg','I\'m 75–95 kg','Over 95 kg'] : ['I weigh under 165 lb','I\'m 165–210 lb','Over 210 lb'];

  // Weight captured. Never pick on weight alone — either we already have skill
  // (→ calculator), or we ask for it.
  function weightRec(kg, label){
    vLastKg = kg; vLastKgLabel = label || (kg + ' kg');
    if (vLastSkill != null) return skillRefine(vLastSkill);
    var fits = sizesFor(kg);
    var words = ['one','two','three','four','five'][fits.length-1] || fits.length;
    if (!fits.length) return [
      { type:'text', text: 'At around <b>'+escapeHtml(vLastKgLabel)+'</b> you\'re at the edge of the range — here\'s the full lineup:' },
      sizeTable(null),
      { type:'text', text: 'Tell me where your riding\'s at and I\'ll get you to the closest fit:' },
      { type:'chips', options: SKILL_CHIPS }
    ];
    return [
      { type:'text', text: fits.length === 1 ? 'That puts you on one size:' : 'That puts you across '+words+' sizes:' },
      rangeStrip(kg),
      { type:'text', text: 'Which one comes down to where your riding\'s at:' },
      { type:'chips', options: SKILL_CHIPS }
    ];
  }
  // Skill captured before weight — hold it and ask for weight.
  function skillAskWeight(skill, lead){
    vLastSkill = skill;
    return [
      { type:'text', text: (lead || 'Noted.') + ' Now your weight — that\'s what sets the volume:' },
      { type:'chips', options: WEIGHT_CHIPS }
    ];
  }

  /* ---- Skill refine: weight (already captured) + skill -> 1–2 fitting Glides.
     Reuses the canonical KB-synced calculator when it's on the page
     (window.WakaSizeCalc, loaded only on the PDP); otherwise an inline mirror.
     ⚠️ KB §8 LOCKSTEP: WING_FALLBACK + V_SPECS below mirror knowledge-base.ts /
     assets/waka-size-calc.js / functions/src/size-calc.ts — if the sizing tables
     change, update all of them together. Do not hand-edit this mirror alone. ---- */
  var WING_FALLBACK = {
    b0: { 0:['77L','88L'],  1:['66L','77L'],  2:['66L'] },          // 55–65
    b1: { 0:['88L','99L'],  1:['77L','88L'],  2:['66L','77L'] },    // 65–75
    b2: { 0:['99L','111L'], 1:['88L','99L'],  2:['77L','88L'] },    // 75–85
    b3: { 0:['99L','111L'], 1:['88L','99L'],  2:['77L','88L'] },    // 85–95
    b4: { 0:['111L'],       1:['99L','111L'], 2:['88L','99L'] }     // 95+
  };
  var V_SPECS = {
    '66L':  [['Length','183 cm'],['Rider','Under 70 kg']],
    '77L':  [['Length','190 cm'],['Rider','65–85 kg']],
    '88L':  [['Length','198 cm'],['Rider','75–95 kg']],
    '99L':  [['Length','206 cm'],['Rider','85–110 kg']],
    '111L': [['Length','213 cm'],['Rider','95 kg+']]
  };
  function vBracket(kg){ return kg<65?'b0':kg<75?'b1':kg<85?'b2':kg<95?'b3':'b4'; }
  function vRecommendWing(kg, skill){
    if (window.WakaSizeCalc && typeof window.WakaSizeCalc.recommend === 'function'){
      var r = window.WakaSizeCalc.recommend(kg, 'wing', skill);
      return (r && r.picks && r.picks.length) ? r.picks.slice() : null;
    }
    if (kg < 55) return ['66L'];
    return (WING_FALLBACK[vBracket(kg)][skill] || []).slice();
  }
  function vSkillName(s){ return s===0?'Beginner':s===2?'Advanced':'Intermediate'; }
  function vSpecsFor(vol){
    var sp = (BOARDS && BOARDS[vol] && BOARDS[vol].specs && BOARDS[vol].specs.length)
      ? BOARDS[vol].specs : (V_SPECS[vol] || []);
    // Skip unseeded metafield rows ("Typical rider —") — an empty cell reads as a gap.
    return sp.filter(function(r){ return r && r[1] && String(r[1]).trim() && String(r[1]).trim() !== '—'; });
  }
  // Weight + skill both in → ask for the spot (third step, skippable), then hand
  // the whole profile to the agent so the answer is location-aware (Nigel
  // 2026-09-02). The canned two-board cards below (skillRefineCards) are kept as
  // the offline fallback only — the proxy renders the same recap/compare parts.
  var vAwaitSpot = false;
  var SPOT_SKIP_CHIP = 'Skip — just size me';
  function skillRefine(skill){
    if (vLastKg == null) return null;            // no weight yet — let the matcher fall through
    vLastSkill = skill; vAwaitSpot = true;
    return [
      { type:'text', text: 'Last one — where do you ride most? A spot, a lake, a coast… it changes which of your two sizes I\'d lean to. Type it, or skip.' },
      { type:'chips', options:[SPOT_SKIP_CHIP] }
    ];
  }
  // The rider answered the spot question (or skipped): rewrite the turn the agent
  // sees into one self-contained profile sentence, then defer to the proxy.
  function handSpotToAgent(text){
    vAwaitSpot = false;
    var spot = /^skip\b|^no\b|^nah\b|^nope\b|^none\b|^-$/.test((text||'').trim().toLowerCase()) ? '' : (text||'').trim();
    vLastSpot = spot;
    // Send the NUMBER, not the chip label — the model won't size "75–95 kg", it
    // asks for an exact weight and the rider never gets their boards (Claude Code
    // smoke test, 2026-09-02). The label stays on the recap card via vLastKgLabel.
    var q = "I'm " + vLastKg + ' kg, winging, ' + vSkillName(vLastSkill).toLowerCase() + (spot ? ', I ride at ' + spot : '') + '. Which size suits me?';
    var last = VIENTO_HISTORY[VIENTO_HISTORY.length - 1];
    if (last && last.role === 'user') last.content = q; else VIENTO_HISTORY.push({ role:'user', content:q });
    vOfflineFallback = skillRefineCards(vLastSkill);   // if the proxy is unreachable, still size them
    // Say what's taking the time from the first millisecond — the server's own
    // status ("Finding your size — and thinking about the wind at …") lands ~2.5 s in.
    vNextTypingLabel = spot ? 'Thinking about the wind at ' + spot + '…' : 'Finding your size…';
    return null;                                 // → proxyAsk(); the visible bubble keeps what the rider typed
  }
  var vOfflineFallback = null;                   // canned parts to render if the very next proxy call fails
  var vLastSpot = '';                            // spot as typed — shown in the fallback recap
  function skillRefineCards(skill){
    var kg = vLastKg;
    if (kg == null) return null;
    var picks = vRecommendWing(kg, skill);
    if (!picks || !picks.length) return null;
    var borderline = (kg >= 85 && kg < 95 && skill === 1);
    var parts = [
      { type:'recap', profile:{ weight: vLastKgLabel || (kg+' kg'), discipline:'Winging', skill: vSkillName(skill), spot: vLastSpot || '' } }
    ];
    if (picks.length === 1){
      parts.push({ type:'compare', header:'Your size', picks:[
        { vol:picks[0], role:'Your size', tone:'perf', why:'The single best fit for your weight and skill.', specs:vSpecsFor(picks[0]) }
      ]});
    } else {
      parts.push({ type:'compare', header:'Your two-board match', picks:[
        { vol:picks[0], role:'Performance', tone:'perf',  why:'Smaller — livelier, stronger wind.', specs:vSpecsFor(picks[0]) },
        { vol:picks[1], role:'Light wind',  tone:'light', why:'Larger — easier, lighter days.',     specs:vSpecsFor(picks[1]) }
      ]});
    }
    parts.push({ type:'text', text: picks.length === 1
      ? 'That\'s the one — the clear fit for your weight and where you\'re at. Want a closer look?'
      : borderline
      ? 'You\'re right on the line between these two — both fit, so it comes down to wind and feel. Happy to talk it through.'
      : 'These differ only by wind and feel — both fit you. Want a closer look at either?' });
    parts.push({ type:'chips', options:['Show me the '+picks[0], 'Foil & wing setup', 'How does shipping work?'] });
    return parts;
  }

  function cannedRespond(text){
    var low = (text||'').toLowerCase();

    // SPOT ANSWER — we just asked where they ride. Whatever they typed (or the
    // skip chip) completes the profile → agent. Must run before every matcher:
    // "Hood River" would otherwise hit the dealer regex, "Lake Garda" the spot gate.
    if (vAwaitSpot) return handSpotToAgent(text);

    // If the previous canned reply was about bags and they now ask a bare sizing
    // question ("what size", "which one"), it's the BAG's size — defer to the agent
    // (get_product_details + history), not the board size-guide. Consume the flag.
    var wasBag = vLastBag; vLastBag = false;
    if (wasBag && /\b(what|which)\b.*\b(size|one)\b|^\s*what size|^\s*which size/.test(low) && !/board|glide|\bkg\b|weigh/.test(low)) return null;

    // A bag RECOMMENDATION / sizing question ("which roller bag for my 99?",
    // "what size bag should I get?") needs board→bag mapping + conversation context
    // — send it to the agent (it has get_product_details + the recorded history),
    // not the static canned card. Generic "show me the Rolling WAKA" still cards.
    var isBagRec = /should i get|which (roller ?bag|bag|size|one)|what size|recommend|fit (my|a)|for my (board|glide|setup|\d)|i (have|ride|got) (a |the )?(\d{2,3}l?\b|glide|board)/.test(low);
    // A bag-sizing question must not fall through to the BOARD size table
    // ("what size bag for my 99?" used to render the Glide spec table, QA 2026-09-02).
    if (isBagRec && /\bbags?\b|roller|rolling waka/.test(low)) return null;

    // SKILL CAPTURE — with weight already in hand → calculator pick; without it →
    // hold the skill and ask for weight. MUST precede the foil/setup matcher
    // (labels contain "foil") and the learning matcher (labels contain "beginner").
    // A genuine gear question that happens to mention a level ("beginner foil
    // setup?") still goes to the proxy.
    var skillHit = null;
    if (/getting up on foil|just getting up|up on (the )?foil|first time|\bbeginner\b|just learning|new to (foil|wing|this)|just start/.test(low)) skillHit = 0;
    else if (/foiling (&|and) gybing|\bgybing\b|\bgybe\b|\bintermed/.test(low)) skillHit = 1;
    else if (/tacking (&|and) pumping|\btacking\b|\bpumping\b|\badvanced\b/.test(low)) skillHit = 2;

    // RICH SIZING → proxy. The canned engine only handles chip-shaped input. If
    // the rider wrote a real sentence — weight AND skill together, a spot / lake /
    // wind, or anything beyond a short phrase — the agent must actually read it:
    // recommend_size (two-board compare) + locations KB, not a keyword auto-reply.
    var hasKgWord = /\d{2,3}\s*(kg|kgs|kilo|lb|lbs|pound)/.test(low) || /under ?75|75.?(–|-|to| ).?95|over ?95/.test(low);
    var hasSpot = /\b(ride|riding|based|live|living|sail|sailing|session|foil)\w*\s+(in|at|on|near|around|out of)\b|\b(lake|river|bay|beach|coast|island|gorge|lagoon|knots|kts|wind(y|s)?|thermal|light wind|strong wind|choppy|flat water|waves?)\b/.test(low);
    var sizingContext = hasKgWord || skillHit != null || /size|volume|which (board|glide)|pick/.test(low);
    if (sizingContext && ((hasKgWord && skillHit != null) || hasSpot || low.length > 48)) return null;

    if (skillHit != null && !/setup|mast|stab|fuselage|front wing|(which|what) (foil|wing)/.test(low)){
      if (vLastKg != null) return skillRefine(skillHit);
      return skillAskWeight(skillHit, skillHit === 0 ? 'Nice — welcome aboard.' : 'Noted.');
    }

    // ORDER / TRACKING / ACCOUNT / LIVE STOCK → proxy (live). Never canned.
    if (/order|track|shipping status|where('?s| is) my|delivery|parcel|my account|serial|in stock|stock level|available|when.*ship/.test(low)) return null;

    // DEALERS → canned (show all, narrow by region)
    if (/dealer|stockist|retailer|demo|try one|try it|in person|near me|where can i (see|try|buy|demo)|shop near|visit a/.test(low)) {
      if (/\bcanada\b|québec|quebec|montreal|toronto|vancouver/.test(low)) {
        return [{ type:'text', text:'In Canada your closest crew is <b>30 Noeuds</b> — they\'ll show you the Glide and the fold in person:' },
          { type:'dealers', items: DEALERS.filter(function(d){return d.cc==='ca';}) },
          { type:'text', text:'Not nearby? We ship right across Canada, and I\'m here to help you choose just like a shop would.' },
          { type:'chips', options:['Show me all dealers','Help me pick a size'] }];
      }
      if (/united states|\bu\.?s\.?a?\b|\bstates\b|oregon|hood river|michigan|florida|california|texas/.test(low)) {
        return [{ type:'text', text:'Here are our US stockists — any of them can get you on a board:' },
          { type:'dealers', items: DEALERS.filter(function(d){return d.cc==='us';}) },
          { type:'text', text:'Tell me your town and I\'ll point you to the closest one.' },
          { type:'chips', options:['Show me all dealers','Help me pick a size'] }];
      }
      if (/australia|new zealand|\bnz\b|\baus\b|europe|\beu\b|\buk\b|britain|england|france|germany/.test(low)) {
        return [{ type:'text', text:'We\'re still building our dealer network over there. For now we ship direct to you — <b>free</b> — and I can help you choose and set up just like a shop would. Whereabouts are you?' },
          { type:'chips', options:['Help me pick a size','Show me all dealers','How does shipping work?'] }];
      }
      return [{ type:'text', text:'Here\'s our whole dealer network — a small, hand-picked crew who know the Glide inside out:' },
        { type:'dealers', items: DEALERS },
        { type:'text', text:'Where are you riding? Tell me your country or town and I\'ll point you to the nearest one.' },
        { type:'chips', options:['Dealers in the US','Dealers in Canada','Dealers in AU / NZ','Dealers in Europe / UK'] }];
    }

    // SPECIFIC BAG PRODUCT → show the product card. MUST precede the generic bag
    // overview below, or the overview's chips ("Show me the Rolling WAKA",
    // "Just the day bag") re-match the generic rule and loop instead of showing
    // the product. Only fire when we actually have the product data from Liquid.
    if (BAGS && BAGS.roller && !isBagRec && /rolling ?waka|roller|travel bag|for (flying|flights?|trips?)|airport|checked? bag/.test(low) && !/day ?bag/.test(low)) {
      vLastBag = true;
      return [{ type:'text', text:'The <b>'+escapeHtml(BAGS.roller.name)+'</b> carries the folded board, foil and wings as one — built to check in as normal baggage.' },
        { type:'snippet', board: BAGS.roller },
        { type:'chips', options:['Just the day bag','Help me pick a board'] }];
    }
    if (BAGS && BAGS.day && !isBagRec && /day ?bag|just the day|everyday|local spot|daily/.test(low)) {
      vLastBag = true;
      return [{ type:'text', text:'The <b>'+escapeHtml(BAGS.day.name)+'</b> is the everyday carry — padded protection for the run to your local spot, and it\'s packed free with every board.' },
        { type:'snippet', board: BAGS.day },
        { type:'chips', options:['Show me the Rolling WAKA','Help me pick a board'] }];
    }

    // BAGS → canned overview (generic "what bag / how do I carry it")
    if (!isBagRec && /\bbags?\b|day ?bag|rolling waka|board ?bag|carry|cover|which bag|travel bag|protect/.test(low)) {
      vLastBag = true;
      return [{ type:'text', text:'Two ways most riders carry a WAKA. For trips and flights, the <b>Rolling WAKA</b> takes the board, foil and wings as one airport-friendly roller. For the daily run to your local spot, the insulated <b>Day Bag</b> is all you need — and it\'s packed free with every board.' },
        { type:'chips', options:['Show me the Rolling WAKA','Just the day bag','Help me pick a board'] }];
    }

    // RETURNS vs WARRANTY → quick rule of thumb (before the specific flows)
    if (/(return|refund).{0,16}(vs|versus|or)\s*warranty|warranty.{0,16}(vs|versus|or)\s*return|returns? or warranty|difference.{0,20}(return|warranty)/.test(low)) {
      return [{ type:'text', text:'Simple rule of thumb: a <b>return</b> is change of mind on an <b>unridden</b> board within 30 days of delivery. If a board you’ve been <b>riding</b> develops a fault, that’s our <b>warranty</b> — travel and airline damage included. Not sure which fits? Tell me what happened and I’ll point you the right way.' },
        { type:'chips', options:['Start a return','Start a warranty claim'] }];
    }
    // START A RETURN → interactive return flow (before the general returns guide)
    if (/(start|begin|do|process|set ?up).{0,14}return|return (workflow|process)|^returns?$|i('?d like| want| wanna).{0,14}return|send (it|the board|my board) back|return (my|the) (board|glide|order)/.test(low)) {
      return [{ type:'text', text:'No worries at all — let’s sort it. Just a couple of quick things and I’ll get it moving.' },
        { type:'returnflow' }];
    }
    // START A WARRANTY CLAIM → interactive claim flow (before the general warranty pane)
    if (/(start|make|file|open|begin|do).{0,16}(warranty )?claim|warranty claim|claim (my|the|a|this)|i think.*(defect|hinge|fault)|my (hinge|board|board's|fold|lock).*(defect|fault|crack|broke|broken|fail)|is (it|this|my board) covered|covered under warranty/.test(low)) {
      return [{ type:'text', text:'Of course — I’ll ask the right questions, check it’s covered, and get it to a human. Let’s start.' },
        { type:'claimflow' }];
    }
    // WARRANTY → canned (verified 1-year cover + claim step)
    if (/warranty|guarantee|defect|faulty|is it covered|covered|claim|delaminat|crack|broke|broken/.test(low)) {
      return [{ type:'text', text:'Here’s the cover at a glance — a genuine manufacturing defect is exactly what it’s for:' },
        { type:'warranty', data: WARRANTY },
        { type:'text', text:'If something’s not right, I can qualify it and start a claim with you right here.' },
        { type:'chips', options:['Start a warranty claim','Returns or warranty?'] }];
    }
    // RETURNS → canned guide (30-day)
    if (/return|refund|money ?back|send it back|changed my mind|not right for me|30.?day/.test(low)) {
      return [{ type:'text', text:'Of course — no rush, and no hard feelings. You’ve got <b>30 days</b> from delivery for a refund or a size swap, as long as it’s <b>unridden</b>, in its <b>original packaging</b>, and sent with your proof of purchase. Boards bought from a dealer are returned through them.' },
        { type:'guide', title:'30-day returns', steps: RETURN_STEPS },
        { type:'chips', options:['Start a return','Returns or warranty?'] }];
    }
    // REGISTER → QR-scan model (no app, no account) with the 3-screen strip
    if (/register|registration|my\.?waka|activate.*board|qr code|scan.*(board|qr|code)|warranty card|set ?up.*account/.test(low)) {
      return [{ type:'text', text:'Easy — it’s all done from your phone, no app to download and no account to set up. Here’s how it looks:' },
        { type:'regscreens' },
        { type:'text', text:'<b>1 · Scan the QR on your board</b>, by the WAKA logo band — it opens registration with your serial already filled in.<br><br><b>2 · Sign in with Apple or Google</b> — your name and email come over.<br><br><b>3 · Confirm where you bought it</b> and tap register. Your warranty’s active from day one. 🤙' },
        { type:'chips', options:['What does the warranty cover?','Start a warranty claim'] }];
    }
    // SHIPPING (general, not order status) → canned region pane
    if (/shipping|postage|how much.*(ship|deliver)|ship (it|to|cost)|ship(ping)? cost|do you ship/.test(low)) {
      return [{ type:'text', text:'Here’s how it works across the markets we serve:' },
        { type:'shipping', regions: SHIP_ALL, foot: SHIP_FOOT },
        { type:'chips', options:['Find a dealer','Help me pick a size'] }];
    }

    // FOIL / WING SETUP → proxy (Viento's foil KB). Defer (renders a foilpair part if the proxy returns one).
    if (/foil|wing|mast|stab|setup|gear|kit|rig/.test(low)) return null;

    // BOARD SNIPPET → canned, only when Liquid injected per-board data (real price + specs)
    if (BOARDS) {
      var volM = low.match(/\b(66|77|88|99|111)\s*l\b/);
      if (!volM && /(tell me|about|spec|details|info|the)\b/.test(low)) volM = low.match(/\b(66|77|88|99|111)\b/);
      if (volM && !/\d+\s*(kg|lb|pound)/.test(low)) {
        var vk = volM[1] + 'L';
        if (BOARDS[vk]) return [
          { type:'text', text:'Here’s the ' + vk + ' at a glance:' },
          { type:'snippet', board: BOARDS[vk] },
          { type:'chips', options:['Compare the sizes','Foil & wing setup','How does shipping work?'] }
        ];
      }
    }

    // BOARDS / BROWSE → canned only if Liquid supplied cards; else defer to proxy (live prices).
    if (/recommend|show me|the boards|browse|see boards|options|what.*(buy|get)/.test(low)) {
      if (CARDS && CARDS.length) {
        return [{ type:'text', text:'Here\'s what I\'d reach for — and a travel bag while you\'re at it. All fold in half.' },
          { type:'cards', items: CARDS },
          { type:'chips', options:['Compare the sizes','I weigh 85 kg','Tell me about the fold'] }];
      }
      return null;
    }

    // WEIGHT-AWARE SIZING — quick specific pick
    var kg = null, kgLabel = null;
    if (/under ?75 ?kg|under ?165 ?lb|lighter|light(er)? rider/.test(low)) { kg = 70;  kgLabel = vMetric ? 'under 75 kg' : 'under 165 lb'; }
    else if (/75.?(–|-|to| ).?95 ?kg|165.?(–|-|to| ).?210 ?lb|mid-?weight/.test(low)) { kg = 85;  kgLabel = vMetric ? '75–95 kg' : '165–210 lb'; }
    else if (/over ?95 ?kg|95 ?kg ?\+|over ?210 ?lb|heavier|heavy rider|100 ?kg/.test(low)) { kg = 100; kgLabel = vMetric ? 'over 95 kg' : 'over 210 lb'; }
    if (kg == null){ var m = low.match(/(\d{2,3})\s*(kg|kgs|kilo)/); if (m) kg = parseInt(m[1],10); }
    if (kg == null){ var lb = low.match(/(\d{2,3})\s*(lb|lbs|pound)/); if (lb){ kg = Math.round(parseInt(lb[1],10)*0.4536); kgLabel = lb[1]+' lb (~'+kg+' kg)'; } }
    if (kg != null && kg >= 35 && kg <= 200) return weightRec(kg, kgLabel);

    // GENERIC SIZING → ask. No table, no highlight, no pick until weight + skill are in.
    if (/size|volume|weigh|kg|lb|pound|how big|66|77|88|99|111|pick.*(size|board)/.test(low)) {
      return [{ type:'text', text:'Here\'s the range — your weight does most of the narrowing:' },
        sizeTable(null),
        { type:'text', text:'So — roughly what do you weigh?' },
        { type:'chips', options: WEIGHT_CHIPS.concat(['I\'m just learning']) }];
    }

    // GREETING → canned
    if (/^(hi|hey|kia ora|hello|yo|g'?day|howdy|morena|sup)\b/.test(low)) {
      return [{ type:'text', text:'Kia ora — I’m Viento, WAKA’s guide. 🤙 Tell me a bit about your riding and I’ll help you find the right Glide.' },
        { type:'chips', options:['Help me pick a size','Show me the boards','Foil & wing setup','Track my order'] }];
    }

    // FOLD / TRAVEL → canned step guide
    if (/fold|travel|fly|airport|pack|boot|car/.test(low)) {
      return [{ type:'text', text:'It’s about a thirty-second job once you’ve done it the first time:' },
        { type:'guide', title:'Folding the Glide', steps: FOLD },
        { type:'text', text:'Folded, it drops to about half its length — the boot of the car, a boat hull, or the overhead bin.' },
        { type:'chips', options:['Which bag for flying?','Help me pick a size'] }];
    }

    // WAVES → no pick without weight + skill. Point them into the size flow.
    if (/waves?|surf/.test(low)) {
      return [{ type:'text', text:'For waves you want the smaller, livelier end of <i>your</i> range — which one that is depends on your weight and where your riding\'s at. Give me both and I\'ll narrow it. First, weight:' },
        { type:'chips', options: WEIGHT_CHIPS }];
    }

    // LEARNING → capture skill, ask weight. (Chip labels are caught by the skill
    // matcher above; this is the free-text fallback.)
    if (/learn|beginn|new to|just start/.test(low)) {
      return skillAskWeight(0, 'Nice — welcome aboard. Starting out, volume is your friend, but the right amount depends on you.');
    }

    // Unknown → proxy
    return null;
  }

  /* ============================================================
     RENDERERS
     ============================================================ */
  function el(tag, cls, html){ var d=document.createElement(tag); if(cls) d.className=cls; if(html!=null) d.innerHTML=html; return d; }
  function VMARK(s){ return '<svg width="'+s+'" height="'+s+'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h11a2.5 2.5 0 1 0-2.5-2.5"/><path d="M3 12h15a2.5 2.5 0 1 1-2.5 2.5"/><path d="M3 16h7"/></svg>'; }
  function scrollDown(){ stream.scrollTop = stream.scrollHeight; }

  function addUser(text){
    var m = el('div','vmsg user'); m.appendChild(el('div','vbody', escapeHtml(text)));
    stream.appendChild(m); scrollDown();
    if (!VIENTO_REPLAYING){
      // Every user turn (canned OR proxy) goes into the model history, so when a
      // later turn hands off to the agent it has the full thread — e.g. the weight
      // given via the canned size flow, before "Foil & wing setup" goes live.
      VIENTO_HISTORY.push({ role:'user', content: text });
      VIENTO_TRANSCRIPT.push({ u: text }); saveSession();
    }
  }

  var lastChipLabels = [];
  // Panes that benefit from the wider panel — auto-expand only when one is present.
  var WIDE_PARTS = { compare:1, snippet:1, foilpair:1, warranty:1, shipping:1, order:1, table:1 };
  function setWide(on){
    panel.classList.toggle('wide', on);
    var b = document.querySelector('[data-viento-expand]');
    if (b){ b.textContent = on ? '⇥' : '⇤'; b.setAttribute('title', on ? 'Restore' : 'Expand'); }
  }
  // Promote markdown pipe-tables inside text parts into structured `table` panes.
  function cellsOf(line){ return line.replace(/^\s*\|/,'').replace(/\|\s*$/,'').split('|').map(function(c){ return c.trim(); }); }
  function mdTableToPart(lines){
    if (lines.length < 3) return null;
    var cols = cellsOf(lines[0]);
    var rows = lines.slice(2).map(function(l){ var c = cellsOf(l); return { name: c[0], num: c.slice(1, -1), use: c[c.length-1] }; }).filter(function(r){ return r.name; });
    return rows.length ? { type: 'table', title: '', cols: cols, rows: rows } : null;
  }
  function promoteTables(parts){
    var out = [];
    parts.forEach(function(p){
      if (p.type !== 'text' || !p.text || !/\n\s*\|?[\s:|-]*-{2,}/.test(p.text)) { out.push(p); return; }
      var lines = p.text.split('\n'), seg = [], i = 0;
      while (i < lines.length){
        var isHdr = /^\s*\|.*\|\s*$/.test(lines[i]) && i+1 < lines.length && /-{2,}/.test(lines[i+1]) && /\|/.test(lines[i+1]);
        if (isHdr){
          if (seg.join('\n').trim()) out.push({ type:'text', text: seg.join('\n').trim() }); seg = [];
          var tbl = [lines[i], lines[i+1]]; i += 2;
          while (i < lines.length && lines[i].trim() && /\|/.test(lines[i])){ tbl.push(lines[i]); i++; }
          var part = mdTableToPart(tbl); out.push(part || { type:'text', text: tbl.join('\n') });
        } else { seg.push(lines[i]); i++; }
      }
      if (seg.join('\n').trim()) out.push({ type:'text', text: seg.join('\n').trim() });
    });
    return out;
  }
  function renderBot(parts){
    if (!parts) return;
    var orig = parts;                 // record the un-promoted parts for replay
    parts = promoteTables(parts);
    var m = el('div','vmsg bot');
    m.appendChild(el('div','vtag','<span class="vt-mk"></span>Viento'));
    parts.forEach(function(p){ var node = renderPart(p); if (node) m.appendChild(node); });
    stream.appendChild(m); scrollDown();
    // Auto-widen the pane when a reply carries a wide pane (only when required).
    if (parts.some(function(p){ return WIDE_PARTS[p.type]; })) setWide(true);
    if (!VIENTO_REPLAYING){ VIENTO_TRANSCRIPT.push({ b: orig }); saveSession(); }
  }

  function renderPart(p){
    if (p.type === 'text') return el('p','vtext', mdLite(p.text));
    if (p.type === 'chips'){
      var c = el('div','vchips'); var shown = [];
      p.options.forEach(function(opt){
        var label = (opt && typeof opt === 'object') ? opt.label : opt;
        if (lastChipLabels.indexOf(label) >= 0) return;   // don't repeat a chip from the previous reply
        var send  = (opt && typeof opt === 'object' && opt.ask) ? opt.ask : label;
        var b = el('button','vchip', escapeHtml(label));
        b.type='button';
        b.addEventListener('click', function(){ ask(send); });
        c.appendChild(b); shown.push(label);
      });
      if (!shown.length) return null;
      lastChipLabels = shown;
      return c;
    }
    if (p.type === 'returnflow') return buildReturnFlow();
    if (p.type === 'claimflow')  return buildClaimFlow();
    if (p.type === 'regscreens') return buildRegScreens();
    if (p.type === 'sizeguide'){
      var sg = el('div','v2-sizeguide');
      function sgRows(){
        return SIZE_GEO.map(function(g,i){
          return '<tr class="'+((p.lift||[]).indexOf(i)!==-1?'rt-hl':'')+'"><td>'+escapeHtml(g.vol)+'</td><td>'+escapeHtml(fmtLen(g))+'</td><td>'+escapeHtml(fmtWid(g))+'</td><td class="rt-best">'+escapeHtml(riderRange(g))+'</td></tr>';
        }).join('');
      }
      function sgHead(){
        return '<div class="rt-h"><span class="rt-mk"></span><span class="rt-t">'+escapeHtml(p.title||'Size guide')+'</span>'+
          '<span class="v2-uswitch" role="group" aria-label="Unit system"><button type="button" data-unit="metric" class="'+(vMetric?'on':'')+'">cm · kg</button><button type="button" data-unit="imperial" class="'+(vMetric?'':'on')+'">in · lb</button></span></div>';
      }
      sg.innerHTML = sgHead()+'<table><thead><tr><th>Volume</th><th>Length</th><th>Width</th><th>Typical rider</th></tr></thead><tbody>'+sgRows()+'</tbody></table>';
      sg.addEventListener('click', function(e){
        var b = e.target.closest('[data-unit]'); if (!b) return;
        vMetric = b.getAttribute('data-unit') === 'metric';
        // re-render every unit-bearing part in the stream so they agree
        Array.prototype.forEach.call(stream.querySelectorAll('.v2-sizeguide, .v2-strip, .v2-picks'), function(node){ if (node.__rerender) node.__rerender(); });
      });
      sg.__rerender = function(){ sg.innerHTML = sgHead()+'<table><thead><tr><th>Volume</th><th>Length</th><th>Width</th><th>Typical rider</th></tr></thead><tbody>'+sgRows()+'</tbody></table>'; };
      return sg;
    }
    if (p.type === 'strip'){
      var stp = el('div','v2-strip');
      stp.__rerender = function(){
        stp.innerHTML = (p.items||[]).map(function(it){ return '<span class="v2-strip-it"><b>'+escapeHtml(it.vol)+'</b><small>'+escapeHtml(riderRange(it.geo))+'</small></span>'; }).join('');
      };
      stp.__rerender();
      return stp;
    }
    if (p.type === 'picks'){
      var pk = el('div','v2-picks');
      pk.__rerender = function(){
        var cols = (p.items||[]).map(function(it){
          var g = it.geo, bd = it.board, isPre = bd && bd.stock === 'preorder';
          var specs = g ? fmtLen(g) + ' · ' + fmtWid(g) + ' · ' + riderRange(g) : '';
          var stockLine = bd ? (isPre ? 'Pre-order' : 'In stock') : '';
          var tagHtml = '<span class="v2-pk-role">'+escapeHtml(it.role||'')+'</span>' + (it.rec ? '<span class="v2-pk-rec">'+escapeHtml(it.tag||'Recommended')+'</span>' : (it.tag ? '<span class="v2-pk-tag">'+escapeHtml(it.tag)+'</span>' : ''));
          var priceHtml = bd ? '<div class="v2-pk-price"><b>'+escapeHtml(bd.price||'')+'</b>'+(stockLine?'<small>'+escapeHtml(stockLine)+'</small>':'')+(bd.url?'<a class="v2-pk-view" href="'+escapeAttr(bd.url)+'">View</a>':'')+'</div>' : '';
          var ctaHtml = bd && bd.url ? '<a class="v2-pk-cta" href="'+escapeAttr(bd.url)+'">'+(isPre?'Pre-order':'Add to cart')+' <span class="ar">→</span></a>' : '';
          return '<div class="v2-pk-col '+escapeAttr(it.tone||'perf')+(it.rec?' is-rec':'')+'">'+
            '<div class="v2-pk-tags">'+tagHtml+'</div>'+
            '<div class="v2-pk-vol">'+escapeHtml(it.vol)+'</div>'+
            '<p class="v2-pk-why">'+escapeHtml(it.why||'')+'</p>'+
            (specs?'<div class="v2-pk-specs">'+escapeHtml(specs)+'</div>':'')+
            priceHtml + ctaHtml + '</div>';
        }).join('');
        pk.innerHTML = '<div class="v2-cmp-h">'+VMARK(13)+escapeHtml(p.header||'Your match')+'</div><div class="v2-pk-grid">'+cols+'</div>'+
          (p.note ? '<div class="v2-cmp-note"><span class="ni">'+VMARK(15)+'</span><p>'+p.note+'</p></div>' : '');
      };
      pk.__rerender();
      return pk;
    }
    if (p.type === 'recap'){
      var rc = el('div','v2-recap');
      var prof = null, items, editable = false;
      if (p.items){ items = p.items.map(function(it){ return [it[0], it[1], null]; }); }   // arbitrary labelled pairs (dual-discipline)
      else { prof = p.profile || {}; editable = true;
        items = [['Weight', prof.weight, 'Add your weight'], ['Discipline', prof.discipline, null], ['Skill', prof.skill, null], ['Home spot', prof.spot, 'Add spot']]; }
      function recapView(){
        return '<span class="rc-mk">'+VMARK(15)+'</span><div class="rc-body">'+
          items.map(function(it){ var v=it[1], ph=it[2];
            return '<span class="rc-it"><span class="k">'+escapeHtml(it[0])+'</span>'+
              (v ? '<span class="v">'+escapeHtml(v)+'</span>' : (ph ? '<span class="v empty">'+escapeHtml(ph)+' +</span>' : '<span class="v">—</span>'))+'</span>';
          }).join('')+'</div>'+(editable ? '<button class="rc-edit" type="button"><span class="pen">✎</span>Edit</button>' : '');
      }
      rc.innerHTML = recapView();
      if (editable){
        rc.addEventListener('click', function(e){
          if (!(e.target.closest('.rc-edit') || e.target.closest('.v.empty'))) return;
          var DISC=['Winging','Parawinging','Downwind','Prone','SUP foil'], SK=['Beginner','Intermediate','Advanced'];
          rc.classList.add('editing');
          function opts(list, cur){ return list.map(function(d){ return '<option'+(d===cur?' selected':'')+'>'+d+'</option>'; }).join(''); }
          rc.innerHTML = '<span class="rc-mk">'+VMARK(15)+'</span><div class="rc-body">'+
            '<span class="rc-field"><span class="k">Weight</span><input data-f="weight" value="'+escapeAttr(prof.weight||'')+'" placeholder="e.g. 95 kg"></span>'+
            '<span class="rc-field"><span class="k">Discipline</span><select data-f="discipline">'+opts(DISC, prof.discipline)+'</select></span>'+
            '<span class="rc-field"><span class="k">Skill</span><select data-f="skill">'+opts(SK, prof.skill)+'</select></span>'+
            '<span class="rc-field"><span class="k">Home spot</span><input data-f="spot" value="'+escapeAttr(prof.spot||'')+'" placeholder="e.g. the Gorge"></span>'+
            '</div><button class="rc-save" type="button">Save</button>';
          rc.querySelector('.rc-save').addEventListener('click', function(){
            var v = {}; Array.prototype.forEach.call(rc.querySelectorAll('[data-f]'), function(f){ v[f.getAttribute('data-f')] = f.value.trim(); });
            // Restore the card to view mode with the edited values — otherwise the
            // old bubble is left showing a bare form + Save (QA 2026-09-02).
            prof.weight = v.weight; prof.discipline = v.discipline; prof.skill = v.skill; prof.spot = v.spot;
            items = [['Weight', prof.weight, 'Add your weight'], ['Discipline', prof.discipline, null], ['Skill', prof.skill, null], ['Home spot', prof.spot, 'Add spot']];
            rc.classList.remove('editing'); rc.innerHTML = recapView();
            var q = "I'm " + (v.weight||'') + ', ' + (v.discipline||'').toLowerCase() + ', ' + (v.skill||'').toLowerCase() + (v.spot ? ', I ride at ' + v.spot : '') + '. Which size suits me?';
            ask(q);
          });
        });
      }
      return rc;
    }
    if (p.type === 'compare'){
      var cmp = el('div','v2-compare');
      var hasStrip = (p.picks||[]).some(function(pk){ return pk.tag || pk.rec; });   // cross-discipline recommended variant
      var grid = (p.picks||[]).map(function(pick){
        var specs = (pick.specs||[]).map(function(s){ return '<div class="sr'+(s[2]?' key':'')+'"><span class="sk">'+escapeHtml(s[0])+'</span><span class="sv">'+escapeHtml(s[1])+'</span></div>'; }).join('');
        var inner = '<div class="v2-cmp-role">'+escapeHtml(pick.role||'')+'</div><div class="v2-cmp-vol">'+escapeHtml(pick.vol||'')+'</div><p class="v2-cmp-why">'+escapeHtml(pick.why||'')+'</p><div class="v2-cmp-specs">'+specs+'</div>';
        if (hasStrip){
          return '<div class="v2-cmp-col has-strip '+(pick.rec?'is-rec':'alt-col')+'">'+
            '<div class="v2-cmp-strip '+(pick.rec?'rec':'alt')+'">'+(pick.rec?'<span class="ck">✓</span>':'')+escapeHtml(pick.tag||'')+'</div>'+
            '<div class="v2-cmp-inner">'+inner+'</div></div>';
        }
        return '<div class="v2-cmp-col '+escapeAttr(pick.tone||'perf')+'">'+inner+'</div>';
      }).join('');
      cmp.innerHTML = '<div class="v2-cmp-h">'+VMARK(13)+escapeHtml(p.header||'Your match')+'</div><div class="v2-cmp-grid">'+grid+'</div>'+
        (p.note ? '<div class="v2-cmp-note"><span class="ni">'+VMARK(15)+'</span><p>'+p.note+'</p></div>' : '');
      return cmp;
    }
    // Key on `cols` (this renderer's own field, with object rows {name,num,use}).
    // NOT `rows` — the canned size-guide tables use `columns` + array rows and must
    // fall through to the `.vrich-table` renderer below; matching on `rows` here
    // intercepted them and rendered empty cells (no r.name/r.use).
    if (p.type === 'table' && p.cols){
      var tbl = el('div','v2-table');
      var thead = '<tr>'+(p.cols||[]).map(function(c){ return '<th>'+escapeHtml(c)+'</th>'; }).join('')+'</tr>';
      var trows = (p.rows||[]).map(function(r){
        var nums = (r.num||[]).map(function(n){ return '<td class="cell-num">'+escapeHtml(n)+'</td>'; }).join('');
        return '<tr'+(r.hl?' class="hl"':'')+'><td class="cell-name">'+escapeHtml(r.name)+'</td>'+nums+'<td class="cell-use">'+escapeHtml(r.use||'')+'</td></tr>';
      }).join('');
      var cap = p.cap ? '<div class="v2-table-cap"><span class="pick">'+escapeHtml(p.cap.pick)+'</span> '+escapeHtml(p.cap.rest)+'</div>' : '';
      tbl.innerHTML = '<div class="v2-table-h">'+VMARK(13)+escapeHtml(p.title||'')+'</div><div class="v2-table-scroll"><table><thead>'+thead+'</thead><tbody>'+trows+'</tbody></table></div>'+cap;
      return tbl;
    }
    if (p.type === 'snippet' && p.board){
      var bd = p.board, isPre = bd.stock === 'preorder';
      var sn = el('div','v2-snippet');
      // vol-less products (bags) skip the volume pill and the "WAKA Glide" name/alt.
      var fullName = (bd.name||'WAKA Glide')+(bd.vol?' '+bd.vol:'');
      var tags = (bd.vol?'<span class="v2-sn-tag">'+escapeHtml(bd.vol)+'</span>':'')+(isPre?'<span class="v2-sn-tag pop">Pre-order</span>':'');
      // Skip unseeded metafield rows ("Typical rider —") — same rule as vSpecsFor().
      var sp = (bd.specs||[]).filter(function(s){ return s && s[1] && String(s[1]).trim() && String(s[1]).trim() !== '—'; })
        .map(function(s){ return '<div class="c"><div class="k">'+escapeHtml(s[0])+'</div><div class="v">'+escapeHtml(s[1])+'</div></div>'; }).join('');
      var kn = (bd.know||[]).map(function(k){ return '<li>'+escapeHtml(k)+'</li>'; }).join('');
      var imgBlock = bd.img
        ? '<div class="v2-sn-img"><img src="'+escapeAttr(bd.img)+'" alt="'+escapeAttr(fullName)+'"><div class="v2-sn-tags">'+tags+'</div></div>'
        : '<div class="v2-sn-img empty"><span class="mk"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="m4 18 5-5 4 3 3-2 4 4"/></svg></span><span class="lab">Photo coming soon</span><div class="v2-sn-tags">'+tags+'</div></div>';
      var flagBlock = bd.flag ? '<div class="v2-sn-flag"><span class="fi"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4M12 17h.01"/><path d="M10.3 3.8 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0z"/></svg></span><span><b>Above the lineup.</b> '+escapeHtml(bd.flag)+'</span></div>' : '';
      sn.innerHTML =
        imgBlock + flagBlock +
        '<div class="v2-sn-body"><div class="v2-sn-top"><div><div class="v2-sn-name">'+escapeHtml(fullName)+'</div><div class="v2-sn-sub">'+escapeHtml(bd.sub||'')+'</div></div>'+
        '<div class="v2-sn-price"><div class="p">'+escapeHtml(bd.price||'')+'</div><div class="pn">'+escapeHtml(bd.priceNote||'')+'</div></div></div>'+
        '<div class="v2-sn-specs">'+sp+'</div>'+(kn?'<div class="v2-sn-know"><div class="kh">Good to know</div><ul>'+kn+'</ul></div>':'')+
        '<div class="v2-sn-foot">'+(bd.url?'<a class="v2-sn-btn solid" href="'+escapeAttr(bd.url)+'">'+(isPre?'Pre-order':'Add to cart')+' <span class="ar">→</span></a><a class="v2-sn-btn ghost" href="'+escapeAttr(bd.url)+'">'+escapeHtml(bd.cta||'View board')+'</a>':'')+'</div></div>';
      return sn;
    }
    if (p.type === 'foilpair' && p.setup){
      var st = p.setup;
      var frows = (st.rows||[]).map(function(r){ return '<div class="v2-foil-row"><span class="fk"><span class="k">'+escapeHtml(r[0])+'</span>'+(r[1]?'<span class="h">'+escapeHtml(r[1])+'</span>':'')+'</span><span class="fv">'+escapeHtml(r[2])+'</span></div>'; }).join('');
      var foilHTML = '<div class="v2-foil"><div class="v2-foil-h">'+VMARK(13)+escapeHtml(st.title||'Foil pairing')+'</div>'+(st.brand?'<div class="v2-foil-brand"><span class="bi"></span><p>'+st.brand+'</p></div>':'')+frows+(st.note?'<div class="v2-foil-note">'+escapeHtml(st.note)+'</div>':'')+'</div>';
      var bbHTML = '';
      if (st.brandBlock){ var d = st.brandBlock;
        bbHTML = '<div class="v2-brandblock"><div class="v2-brandblock-h"><span class="t">'+escapeHtml(d.title||'')+'</span><span class="tag">'+escapeHtml(d.tag||'')+'</span></div>'+
          (d.rows||[]).map(function(r){ return '<div class="v2-bb-row"><span class="cond">'+escapeHtml(r.cond)+'<small>'+escapeHtml(r.sub||'')+'</small></span><span class="rig">'+escapeHtml(r.rig)+'</span></div>'; }).join('')+
          '<div class="v2-bb-tail"><span class="ar">→</span><p>'+escapeHtml(d.tail||'')+'</p></div></div>';
      }
      return el('div', null, foilHTML + bbHTML);
    }
    if (p.type === 'warranty' && p.data){
      var w = p.data, wr = el('div','v2-warr');
      var cov = (w.covered||[]).map(function(c){ return '<li class="'+(c.y?'yes':'no')+'"><span class="ci">'+(c.y?'✓':'–')+'</span>'+escapeHtml(c.t)+'</li>'; }).join('');
      var per = (w.period||[]).map(function(pp){ return '<div class="pc"><div class="pv">'+escapeHtml(pp[0])+'</div><div class="pl">'+escapeHtml(pp[1])+'</div></div>'; }).join('');
      wr.innerHTML = '<div class="v2-warr-h"><span class="v2-warr-badge '+escapeAttr(w.verdict||'ok')+'"></span><div><div class="wt">'+escapeHtml(w.title||'')+'</div><div class="ws">'+escapeHtml(w.sub||'')+'</div></div></div>'+
        '<div class="v2-warr-cov"><div class="ch">What the warranty covers</div><ul>'+cov+'</ul></div><div class="v2-warr-period">'+per+'</div>'+
        '<div class="v2-warr-step"><span class="si">Next</span><p>'+(w.step||'')+'</p></div>';
      return wr;
    }
    if (p.type === 'guide'){
      var g = el('div','v2-guide');
      var gsteps = (p.steps||[]).map(function(s){ return '<div class="v2-guide-step"><span class="v2-guide-num">'+escapeHtml(String(s.n))+'</span><div class="v2-guide-tx"><div class="t">'+escapeHtml(s.t)+'</div><div class="d">'+(s.d||'')+'</div></div></div>'; }).join('');
      g.innerHTML = '<div class="v2-guide-h">'+VMARK(13)+escapeHtml(p.title||'')+'</div>'+gsteps;
      return g;
    }
    if (p.type === 'shipping'){
      var sh = el('div','v2-ship');
      var srows = (p.regions||[]).map(function(r){ return '<div class="v2-ship-row"><img class="v2-ship-flag" src="https://flagcdn.com/w160/'+encodeURIComponent(r.cc)+'.png" alt=""><span class="v2-ship-rg"><span class="rn">'+escapeHtml(r.name)+'</span>'+(r.note?'<span class="rnote">'+escapeHtml(r.note)+'</span>':'')+'</span><span class="v2-ship-meta"><span class="cost">'+escapeHtml(r.cost)+'</span><span class="eta">'+escapeHtml(r.eta)+'</span></span></div>'; }).join('');
      sh.innerHTML = '<div class="v2-ship-h">Shipping &amp; delivery</div>'+srows+(p.foot?'<div class="v2-ship-foot">'+p.foot+'</div>':'');
      return sh;
    }
    if (p.type === 'table'){
      var wrap = el('div','vrich-table');
      wrap.appendChild(el('div','rt-h', '<span class="rt-mk"></span>'+escapeHtml(p.title)));
      var t = el('table');
      var thead = '<thead><tr>'+p.columns.map(function(c){return '<th>'+escapeHtml(c)+'</th>';}).join('')+'</tr></thead>';
      var bestCol = (p.bestCol == null) ? 1 : p.bestCol;
      var hlSet = Array.isArray(p.highlight) ? p.highlight : (p.highlight == null || p.highlight < 0 ? [] : [p.highlight]);
      var rows = p.rows.map(function(r,ri){
        var cells = r.map(function(cell,ci){ return '<td class="'+(ci===bestCol?'rt-best':'')+'">'+escapeHtml(cell)+'</td>'; }).join('');
        return '<tr class="'+(hlSet.indexOf(ri)!==-1?'rt-hl':'')+'">'+cells+'</tr>';
      }).join('');
      t.innerHTML = thead + '<tbody>'+rows+'</tbody>';
      wrap.appendChild(t);
      return wrap;
    }
    if (p.type === 'dealers'){
      var dw = el('div','vrich-dealers');
      p.items.forEach(function(d){
        var row = el('div','vdealer');
        row.innerHTML =
          '<img class="vd-flag" src="https://flagcdn.com/w160/'+encodeURIComponent(d.cc)+'.png" alt="">'+
          '<span class="vd-info"><span class="vd-name">'+escapeHtml(d.name)+'</span><span class="vd-loc">'+escapeHtml(d.loc)+'</span></span>'+
          '<span class="vd-go">→</span>';
        if (d.url){
          row.style.cursor='pointer'; row.setAttribute('role','link'); row.setAttribute('tabindex','0');
          var openDealer = function(){ window.open(d.url, '_blank', 'noopener'); };
          row.addEventListener('click', openDealer);
          row.addEventListener('keydown', function(e){ if(e.key==='Enter'||e.key===' '){ e.preventDefault(); openDealer(); } });
        }
        dw.appendChild(row);
      });
      return dw;
    }
    if (p.type === 'cards'){
      var cw = el('div','vrich-cards');
      p.items.forEach(function(it){
        var card = el('div','vcard');
        card.innerHTML =
          '<div class="vcard-img"><img src="'+escapeAttr(it.img)+'" alt="'+escapeAttr(it.name)+'"></div>'+
          '<div class="vcard-body">'+
            '<div class="vcard-top"><span class="vcard-name">'+escapeHtml(it.name)+'</span>'+(it.rec?'<span class="vcard-rec">Pick</span>':'')+'</div>'+
            '<div class="vcard-meta">'+escapeHtml(it.meta||'')+'</div>'+
            '<p class="vcard-desc">'+escapeHtml(it.desc||'')+'</p>'+
            '<div class="vcard-foot"><span class="vcard-price">'+escapeHtml(it.price||'')+'</span><span class="vcard-cta">View →</span></div>'+
          '</div>';
        if (it.url){ var cta = card.querySelector('.vcard-cta'); cta.style.cursor='pointer'; cta.addEventListener('click', function(){ window.location.href = it.url; }); }
        cw.appendChild(card);
      });
      return cw;
    }
    // order → native order pane (renders only the fields present — no fabricated ETA/timeline)
    if (p.type === 'order' && p.order)  return renderOrder(p.order);
    // setup is legacy/unknown structured data — render generically as a last resort
    if (p.type === 'setup' && p.setup)  return el('div','vrich-setup', renderKeyVals(p.setup));
    return null;
  }

  // Native order pane. Tolerant of the honest, minimal order shape the proxy sends
  // (id · ordered/placed · status · items · total · carrier); shows timeline/eta only if present.
  function renderOrder(o){
    var placed = o.placed || o.ordered || '';
    var html = '<div class="vorder-head"><div class="vorder-id">'+escapeHtml(o.id||'Order')+(placed?'<small>'+escapeHtml(placed)+'</small>':'')+'</div>'+(o.status?'<span class="vorder-badge">'+escapeHtml(o.status)+'</span>':'')+'</div>';
    if (o.steps && o.steps.length){
      html += '<div class="vorder-track">'+o.steps.map(function(st,i){ var cls = i < o.current ? 'done' : (i === o.current ? 'curr' : ''); return '<div class="vstep '+cls+'"><span class="vsd"></span><span class="vsl">'+escapeHtml(st)+'</span></div>'; }).join('')+'</div>';
    }
    if (o.eta) html += '<div class="vorder-eta">🤙 <b>'+escapeHtml(o.eta)+'</b></div>';
    if (o.items && o.items.length){
      html += '<div class="vorder-items">'+o.items.map(function(it){
        if (typeof it === 'string') return '<div class="voi"><span class="voi-n">'+escapeHtml(it)+'</span></div>';
        return '<div class="voi"><span class="voi-n">'+escapeHtml(it.n||'')+'</span>'+(it.s?'<span class="voi-s">'+escapeHtml(it.s)+'</span>':'')+'</div>';
      }).join('')+'</div>';
    }
    if (o.total) html += '<div class="vorder-total"><span>Total</span><b>'+escapeHtml(o.total)+'</b></div>';
    if (o.carrier) html += '<div class="vorder-carrier">'+escapeHtml(o.carrier)+'</div>';
    return el('div','vrich-order', html);
  }

  function renderKeyVals(o){
    try { return '<pre class="vraw">'+escapeHtml(JSON.stringify(o, null, 2))+'</pre>'; }
    catch(e){ return ''; }
  }

  /* ============================================================
     INTERACTIVE FLOWS (stateful widgets) — refresh-5.
     returnflow / claimflow are mock starters: the order check is
     illustrative; the live RMA / claim ticket happen in the proxy
     behind auth (see DEBUG-STATUS UPDATE — support round).
     ============================================================ */
  function normOrder(v){ return v.toUpperCase().replace(/\s+/g,'').replace(/^WK-?/,'WK-'); }
  function dealerRows(list){
    return list.map(function(d){
      return '<div class="vdealer"><img class="vd-flag" src="https://flagcdn.com/w160/'+escapeAttr(d.cc)+'.png" alt="">'+
        '<span class="vd-info"><span class="vd-name">'+escapeHtml(d.name)+'</span><span class="vd-loc">'+escapeHtml(d.loc)+'</span></span></div>';
    }).join('');
  }

  // --- return flow: origin → (ordernum|dealer) → verified → (refund|swap→swapdone), notfound branch
  function buildReturnFlow(){
    var root = el('div','vrich-return', '<div class="vr-head"><span class="vr-mk">'+VMARK(13)+'</span>Start a return</div><div class="vr-body"></div>');
    var body = root.querySelector('.vr-body');
    var st = { step:'origin', num:'', order:'', swap:'' };
    function on(sel, ev, fn){ var n = body.querySelector(sel); if (n) n.addEventListener(ev, fn); }
    function onAll(sel, ev, fn){ Array.prototype.forEach.call(body.querySelectorAll(sel), function(n){ n.addEventListener(ev, fn); }); }
    function check(){
      var v = (body.querySelector('.vr-in') ? body.querySelector('.vr-in').value : st.num).trim();
      st.num = v; if (!v) return;
      if (WK_RE.test(v)){ st.order = normOrder(v); st.step='verified'; } else { st.step='notfound'; }
      render();
    }
    function render(){
      var s = st.step, h = '';
      if (s === 'origin'){
        h = '<p class="vr-q">First up — where did you buy your Glide? It decides who looks after the return.</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="ordernum"><b>Direct from waka.surf</b><span>I ordered on the website</span></button>'+
          '<button class="vr-opt" data-go="dealer"><b>From a dealer or retailer</b><span>A shop or stockist sold it to me</span></button></div>';
      } else if (s === 'dealer'){
        h = '<p class="vr-q">No worries. Boards bought through a dealer are returned <b>through them</b> — they handled the sale, so they’ll sort your refund or swap under the same <b>30-day</b> terms. Here’s our crew:</p>'+
          '<div class="vrich-dealers vr-dealers">'+dealerRows(DEALERS)+'</div>'+
          '<button class="vr-back" data-go="ordernum">Actually, I ordered on waka.surf →</button>';
      } else if (s === 'ordernum'){
        h = '<p class="vr-q">Pop in your order number — it’s at the top of your confirmation email, like <b>WK-10482</b>. I’ll check it’s one of ours.</p>'+
          '<div class="vr-input"><input class="vr-in" value="'+escapeAttr(st.num)+'" placeholder="WK-10482" aria-label="Order number"><button class="vr-check-btn" type="button">Check</button></div>'+
          '<button class="vr-back" data-go="origin">← I bought from a dealer</button>';
      } else if (s === 'notfound'){
        h = '<p class="vr-q">Hmm — <b>'+escapeHtml(st.num)+'</b> doesn’t look like a waka.surf order number. Ours are like <b>WK-10482</b>. If you bought through a dealer, they’ll handle the return for you.</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="ordernum-retry"><b>Try the number again</b></button>'+
          '<button class="vr-opt" data-go="dealer"><b>I bought from a dealer</b></button></div>';
      } else if (s === 'verified'){
        h = '<div class="vr-verified"><span class="vr-ok">✓</span><div><b>'+escapeHtml(st.order)+'</b> — found it, and it’s within your <b>30-day</b> window.<small>Illustrative check · the live assistant reads your real waka.surf order.</small></div></div>'+
          '<p class="vr-q">What would suit you best?</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="refund"><b>A refund</b><span>Send it back, money back</span></button>'+
          '<button class="vr-opt" data-go="swap"><b>Swap the size</b><span>Same price, straight exchange</span></button>'+
          '<button class="vr-opt" data-ask="Help me pick the right size before I decide"><b>Help me decide first</b><span>A quick sizing chat</span></button></div>';
      } else if (s === 'refund'){
        h = '<p class="vr-q">Done — I’ve flagged <b>'+escapeHtml(st.order)+'</b> to the crew. Before it ships back, just make sure it’s:</p>'+
          '<ul class="vr-check"><li>Unridden — no marks on the deck, hull or foil track</li>'+
          '<li>In its original packaging, with the day bag, fasteners and printed bits</li>'+
          '<li>Sent with your proof of purchase</li></ul>'+
          '<p class="vr-note">You’ll get a <b>return label</b> and the address by email within a day. Once it’s back and checked, we refund to your original payment — usually a few business days after. 🤙</p>';
      } else if (s === 'swap'){
        h = '<p class="vr-q">Easy — every Glide’s the same price, so it’s a straight swap. Which size are you after?</p>'+
          '<div class="vr-sizes">'+SWAP_SIZES.map(function(sz){ return '<button class="vr-size'+(st.swap===sz?' on':'')+'" data-size="'+escapeAttr(sz)+'">'+escapeHtml(sz)+'</button>'; }).join('')+'</div>'+
          '<button class="vr-back" data-ask="Which size should I swap to? Help me pick">Not sure which? Ask me →</button>';
      } else if (s === 'swapdone'){
        h = '<div class="vr-verified"><span class="vr-ok">✓</span><div>Swapping <b>'+escapeHtml(st.order)+'</b> to the <b>'+escapeHtml(st.swap)+'</b>.</div></div>'+
          '<p class="vr-note">I’ll get the <b>'+escapeHtml(st.swap)+'</b> moving as soon as the original’s on its way back — same price, nothing more to pay. Your return label and tracking land by email within a day. 🤙</p>';
      }
      body.innerHTML = h;
      onAll('[data-go]', 'click', function(){ var g = this.getAttribute('data-go'); if (g==='ordernum-retry'){ st.num=''; st.step='ordernum'; } else { st.step=g; } render(); });
      onAll('[data-ask]', 'click', function(){ ask(this.getAttribute('data-ask')); });
      onAll('.vr-size', 'click', function(){ st.swap = this.getAttribute('data-size'); st.step='swapdone'; render(); });
      on('.vr-check-btn', 'click', check);
      on('.vr-in', 'keydown', function(e){ if (e.key === 'Enter') check(); });
      scrollDown();
    }
    render();
    return root;
  }

  // --- warranty claim flow: issue (cover verdict) → ordernum → opened, with notcovered/transit branches
  function buildClaimFlow(){
    var root = el('div','vrich-return', '<div class="vr-head"><span class="vr-mk">'+VMARK(13)+'</span>Warranty claim</div><div class="vr-body"></div>');
    var body = root.querySelector('.vr-body');
    var st = { step:'issue', num:'', order:'' };
    function onAll(sel, ev, fn){ Array.prototype.forEach.call(body.querySelectorAll(sel), function(n){ n.addEventListener(ev, fn); }); }
    function on(sel, ev, fn){ var n = body.querySelector(sel); if (n) n.addEventListener(ev, fn); }
    function check(){
      var v = (body.querySelector('.vr-in') ? body.querySelector('.vr-in').value : st.num).trim();
      st.num = v; if (!v) return;
      if (WK_RE.test(v)){ st.order = normOrder(v); st.step='opened'; } else { st.step='noorder'; }
      render();
    }
    function render(){
      var s = st.step, h = '';
      if (s === 'issue'){
        h = '<p class="vr-q">Sorry to hear it — let’s see what’s going on. What’s the issue?</p>'+
          '<div class="vr-opts">'+CLAIM_ISSUES.map(function(it){ return '<button class="vr-opt" data-k="'+escapeAttr(it.k)+'"><b>'+escapeHtml(it.t)+'</b></button>'; }).join('')+'</div>';
      } else if (s === 'notcovered'){
        h = '<p class="vr-q">That sounds like <b>impact or wear</b> rather than a manufacturing fault, so it sits outside the warranty — a hard life on the water is normal. The good news: the hinge, fasteners and foil-track hardware are <b>serviceable</b>, so it’s often a quick paid repair or parts swap, not a new board.</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="ordernum"><b>Get a repair quote</b><span>Send it through anyway</span></button>'+
          '<button class="vr-opt" data-go="issue"><b>Actually, it’s a defect</b></button></div>';
      } else if (s === 'transit'){
        h = '<p class="vr-q">Damage in transit by an airline or courier isn’t covered by the warranty — but we won’t leave you stranded. First, <b>file a claim with the carrier</b> (they’re liable), and photograph the board and packaging. Then send it our way and we’ll help with the assessment, parts or a repair quote.</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="ordernum"><b>Send it to the crew</b><span>We’ll help with parts &amp; repair</span></button></div>';
      } else if (s === 'ordernum'){
        h = '<p class="vr-q">Let’s get it on file. What’s your order or serial number? The order’s at the top of your confirmation email, like <b>WK-10482</b>; the serial’s etched on the bulkhead.</p>'+
          '<div class="vr-input"><input class="vr-in" value="'+escapeAttr(st.num)+'" placeholder="WK-10482" aria-label="Order or serial number"><button class="vr-check-btn" type="button">Next</button></div>'+
          '<button class="vr-back" data-go="issue">← Back</button>';
      } else if (s === 'noorder'){
        h = '<p class="vr-q">Hmm — <b>'+escapeHtml(st.num)+'</b> doesn’t match a waka.surf order (they’re like <b>WK-10482</b>). If you registered by scanning the QR on your board, I can find it by serial — or pop the order number from your confirmation email.</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-go="ordernum-retry"><b>Try again</b></button></div>';
      } else if (s === 'opened'){
        h = '<div class="vr-verified"><span class="vr-ok">✓</span><div>Claim opened against <b>'+escapeHtml(st.order)+'</b>.<small>Illustrative — the live assistant logs a real claim and loops in a human.</small></div></div>'+
          '<p class="vr-q">Last thing — send a <b>photo or two</b> of the issue in good light, and a line on what happened. A human on our crew reviews it, usually within a day or two. If it’s a covered defect we repair or replace it and cover return shipping. 🤙</p>'+
          '<div class="vr-opts"><button class="vr-opt" data-ask="How do I register my board?"><b>Register my board</b><span>Faster claims next time</span></button></div>';
      }
      body.innerHTML = h;
      onAll('[data-k]', 'click', function(){
        var k = this.getAttribute('data-k'), it = null;
        CLAIM_ISSUES.forEach(function(x){ if (x.k===k) it = x; });
        if (!it) return;
        st.step = it.cover === 'yes' ? 'ordernum' : (it.cover === 'transit' ? 'transit' : 'notcovered');
        render();
      });
      onAll('[data-go]', 'click', function(){ var g = this.getAttribute('data-go'); if (g==='ordernum-retry'){ st.num=''; st.step='ordernum'; } else { st.step=g; } render(); });
      onAll('[data-ask]', 'click', function(){ ask(this.getAttribute('data-ask')); });
      on('.vr-check-btn', 'click', check);
      on('.vr-in', 'keydown', function(e){ if (e.key === 'Enter') check(); });
      scrollDown();
    }
    render();
    return root;
  }

  // --- registration screens: visual-only 3-screen QR-scan strip
  var REG_QR = [1,1,1,0,1,1, 1,0,1,0,0,1, 1,1,1,0,1,0, 0,0,0,1,0,1, 1,0,1,0,1,1, 1,1,0,1,0,1];
  function buildRegScreens(){
    var qr = REG_QR.map(function(v){ return '<span'+(v?' class="on"':'')+'></span>'; }).join('');
    var h = '<div class="vreg-strip">'+
      '<div class="vreg-screen"><div class="vreg-ph scan"><div class="vreg-qr">'+qr+'</div><div class="vreg-scanlbl">On your board</div></div><div class="vreg-cap"><b>1 · Scan</b>the QR code</div></div>'+
      '<div class="vreg-screen"><div class="vreg-ph signin"><div class="vreg-logo">waka<i>.</i></div><div class="vreg-btn dark">Apple</div><div class="vreg-btn light">Google</div></div><div class="vreg-cap"><b>2 · Sign in</b>Apple / Google</div></div>'+
      '<div class="vreg-screen"><div class="vreg-ph done"><div class="vreg-check">✓</div><div class="vreg-donelbl">Registered</div><div class="vreg-donesub">Warranty active</div></div><div class="vreg-cap"><b>3 · Done</b>On your account</div></div>'+
      '</div>';
    return el('div','vrich-reg', h);
  }

  /* ---- typing indicator ---- */
  function showTyping(label){
    var m = el('div','vmsg bot');
    m.appendChild(el('div','vtag','<span class="vt-mk"></span>Viento'));
    m.appendChild(el('div','vtyping','<i></i><i></i><i></i>'+(label ? '<span class="vtyping-label">'+escapeHtml(label)+'</span>' : '')));
    stream.appendChild(m); scrollDown();
    return m;
  }
  function removeTyping(node){ if (node && node.parentNode) node.parentNode.removeChild(node); }
  function typingThen(fn){ var t = showTyping(); setTimeout(function(){ removeTyping(t); fn(); }, 560); }

  /* ---- escaping ---- */
  function escapeHtml(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
  // Markdown-lite for proxy text parts: **bold** and [label](url) → HTML. Canned text already
  // uses inline HTML (<b>, <span class="url">), which passes through untouched (no ** or []() in it).
  function mdLite(s){
    if (s == null) return '';
    s = String(s);
    s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, function(_, t, u){ return '<a class="vlink" href="' + u.replace(/"/g,'%22') + '" target="_blank" rel="noopener noreferrer">' + t + '</a>'; });
    s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
    s = linkifyBare(s);
    return s;
  }
  // Turn a bare URL (waka.surf/... or http(s)://...) typed in running text into a
  // tidy clickable link, instead of the raw URL on screen. For WAKA pages the label
  // is the humanised last path segment ("warranty-policy" → "Warranty policy"). The
  // leading-char guard skips URLs already inside an attribute/<a> (preceded by " or >).
  function linkifyBare(s){
    return s.replace(/(^|[\s(])((?:https?:\/\/)?(?:www\.)?waka\.surf\/[^\s<)"']+|https?:\/\/[^\s<)"']+)/gi, function(_, pre, raw){
      var trail = ''; var pm = raw.match(/[.,;:!?]+$/); if (pm){ trail = pm[0]; raw = raw.slice(0, -trail.length); }
      var href = /^https?:\/\//i.test(raw) ? raw : 'https://' + raw.replace(/^www\./i, '');
      var label;
      if (/waka\.surf\//i.test(raw)){
        var seg = raw.replace(/[?#].*$/, '').split('/').filter(Boolean).pop() || raw;
        label = seg.replace(/\.[a-z]+$/i, '').replace(/[-_]/g, ' ');
        label = label.charAt(0).toUpperCase() + label.slice(1);
      } else {
        label = raw.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
      }
      return pre + '<a class="vlink" href="' + href.replace(/"/g, '%22') + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(label) + '</a>' + trail;
    });
  }
  function escapeAttr(s){ return escapeHtml(s).replace(/"/g,'&quot;'); }

  /* ============================================================
     WIRING
     ============================================================ */
  // launcher / close / scrim
  document.querySelectorAll('[data-viento-open]').forEach(function(b){ b.addEventListener('click', function(){ open(); }); });
  document.querySelectorAll('[data-viento-close]').forEach(function(b){ b.addEventListener('click', close); });
  if (scrim) scrim.addEventListener('click', close);
  // widen / restore the panel (spec: vp-expand toggles .wide)
  document.querySelectorAll('[data-viento-expand]').forEach(function(b){
    b.addEventListener('click', function(){
      var p = document.getElementById('VientoPanel'); if (!p) return;
      var wide = p.classList.toggle('wide');
      b.textContent = wide ? '⇥' : '⇤';
      b.setAttribute('title', wide ? 'Restore' : 'Expand');
    });
  });

  // composer
  var sendBtn = document.querySelector('[data-viento-send]');
  if (sendBtn) sendBtn.addEventListener('click', function(){ var v=input.value.trim(); if(v){ input.value=''; ask(v); } });
  if (input) input.addEventListener('keydown', function(e){ if(e.key==='Enter'){ var v=input.value.trim(); if(v){ input.value=''; ask(v); } } });

  // PDP / inline buttons: data-viento-ask="…" → ask()
  // Guard against double-binding (same as the seed handler below) — viento.js can
  // initialise more than once per page, and an unguarded document listener stacks,
  // firing ask() twice on a single click (the duplicate-query bug).
  if (!window.__vientoAskBound){
    window.__vientoAskBound = true;
    document.addEventListener('click', function(e){
      var a = e.target.closest && e.target.closest('[data-viento-ask]');
      if (a){ ask(a.getAttribute('data-viento-ask')); }
    });
  }

  // Inline seed-prompt links in running copy: <a class="viento-link" data-viento-seed="…">Viento</a>
  if (!window.__vientoSeedBound){
    window.__vientoSeedBound = true;
    document.addEventListener('click', function(e){
      var el2 = e.target.closest && e.target.closest('[data-viento-seed]');
      if (el2){ e.preventDefault(); seed(el2.getAttribute('data-viento-seed')); }
    });
    document.addEventListener('keydown', function(e){
      if ((e.key==='Enter'||e.key===' ') && e.target.matches && e.target.matches('[data-viento-seed]')){
        e.preventDefault(); seed(e.target.getAttribute('data-viento-seed'));
      }
    });
  }

  // Summarise a seeded parts array (recap/compare/text) into a compact assistant
  // turn for the model history. Without this, the PDP size-picker handoff is
  // render-only: if the rider then TYPES a follow-up ("i ride in la ventana…")
  // instead of clicking a pre-filled chip, the proxy gets a history with no
  // weight/discipline/skill/picks and the agent restarts the sizing interview.
  function seedSummary(parts){
    var bits = [];
    (parts || []).forEach(function(p){
      if (p.type === 'recap' && p.profile){
        var pr = p.profile;
        bits.push('Rider profile from the size guide — weight: ' + (pr.weight || 'not given')
          + ', discipline: ' + (pr.discipline || 'not given')
          + ', skill: ' + (pr.skill || 'not given')
          + ', home spot: ' + (pr.spot && pr.spot !== '—' ? pr.spot : 'not given') + '.');
      } else if (p.type === 'picks' && p.items && p.items.length){
        bits.push(p.items.length === 1 ? 'Recommended size: ' + p.items[0].vol + '.'
          : 'Two-board match: ' + p.items.map(function(k){ return k.vol + ' (' + k.role + (k.rec ? ', recommended' : '') + ')'; }).join(' / ') + '.');
      } else if (p.type === 'compare' && p.picks && p.picks.length){
        bits.push(p.picks.length === 1
          ? 'Recommended size: ' + p.picks[0].vol + '.'
          : 'Two-board match: ' + p.picks.map(function(k){ return k.vol + ' (' + k.role + ')'; }).join(' / ') + '.');
      } else if (p.type === 'text' && p.text){
        bits.push(p.text);
      }
    });
    return bits.join(' ');
  }
  // Render a ready-made parts array instantly (no proxy) — used by the PDP size
  // picker's handoff to show recap + compare + chips. See VIENTO-RESPONSE-INTEGRATION-PLAN.md.
  // The seeded context is ALSO recorded into the model history (as an assistant
  // turn) so a later live turn inherits the sizing context. History MUST keep
  // strict user/assistant alternation starting with user — the Anthropic API
  // 400s on a leading assistant turn ('first message must use the "user" role')
  // — so an empty history gets a real user anchor first, and a trailing
  // assistant turn gets the summary MERGED in rather than stacked.
  function seedParts(parts){
    open(); renderBot(withBoardCards(parts)); scrollDown();   // PDP hand-off gets the merged pick cards too
    if (!VIENTO_REPLAYING){
      var t = seedSummary(parts);
      if (t){
        var last = VIENTO_HISTORY[VIENTO_HISTORY.length - 1];
        if (!VIENTO_HISTORY.length){
          VIENTO_HISTORY.push({ role:'user', content: 'I used the size guide on the product page.' });
          VIENTO_HISTORY.push({ role:'assistant', content: t });
        } else if (last.role === 'assistant'){
          last.content += '\n\n' + t;
        } else {
          VIENTO_HISTORY.push({ role:'assistant', content: t });
        }
        saveSession();
      }
    }
  }
  window.Viento = { open:open, close:close, ask:ask, seed:seed, seedParts:seedParts };

  // Restore any in-tab conversation (e.g. after viewing a board and coming back).
  restoreSession();

  // ── Deep links (2026-07-22, Nigel) ────────────────────────────────────────
  // External links (emails, socials, QR codes) can open Viento WITH CONTEXT on
  // any waka.surf page:
  //   https://waka.surf/<any-page>?viento=open              → just open the pane
  //   …?viento=open&ask=<urlencoded question>               → open + live answer
  //   …?viento=open&seed=<urlencoded question>              → open + canned/seeded
  //   (`message` accepted as an alias of `ask` — matches the standalone chat
  //    page contract at waka-agent.web.app.)
  // First use: the demo-day ride-summary email's "Ask Viento about your board"
  // link, which lands on the board PDP and asks with the ride context baked in.
  // The params are stripped from the URL afterwards so refresh/share doesn't
  // re-trigger; guarded so double-initialisation can't fire twice.
  if (!window.__vientoDeepLinkBound){
    window.__vientoDeepLinkBound = true;
    try {
      var dlParams = new URLSearchParams(window.location.search || '');
      if (dlParams.get('viento') === 'open'){
        var dlAsk  = dlParams.get('ask') || dlParams.get('message');
        var dlSeed = dlParams.get('seed');
        // Let the page (and PDP context like WAKA_VIENTO_DATA) settle first.
        setTimeout(function(){
          if (dlAsk)      { ask(dlAsk); }        // URLSearchParams already decoded it
          else if (dlSeed){ seed(dlSeed); }
          else            { open(); }
        }, 600);
        // Strip viento params, keep everything else (variant selectors etc.).
        ['viento','ask','seed','message'].forEach(function(k){ dlParams.delete(k); });
        var dlClean = window.location.pathname + (dlParams.toString() ? '?' + dlParams.toString() : '') + window.location.hash;
        window.history.replaceState({}, document.title, dlClean);
      }
    } catch (e) { /* never break the page over a deep link */ }
  }
})();
